/**
 * Portal-side mirror of the backend `TriggerInputType`
 * (`business-layer/domain/workflow/types.ts`), kept identical by hand for want of a shared
 * package — the same duplication the SLA routing policy types live with.
 */
export enum WorkflowTriggerType {
  InboundPhoneCall = 'InboundPhoneCall',
  InboundSms = 'InboundSms',
}

/** Display order for the surfaces that render one row per trigger. */
export const ORDERED_WORKFLOW_TRIGGER_TYPES: readonly WorkflowTriggerType[] = [
  WorkflowTriggerType.InboundPhoneCall,
  WorkflowTriggerType.InboundSms,
];

export const WORKFLOW_TRIGGER_LABELS: Record<WorkflowTriggerType, string> = {
  [WorkflowTriggerType.InboundPhoneCall]: 'Incoming call',
  [WorkflowTriggerType.InboundSms]: 'Incoming SMS',
};

/**
 * Mirrors the backend's `NodeType.Trigger`. Declared rather than imported so this module stays
 * free of page-level dependencies.
 */
const TRIGGER_NODE_TYPE = 'trigger';

interface DefinitionShape {
  nodes?: { type?: unknown; data?: { triggerType?: unknown } }[];
}

/**
 * Reads the trigger type out of a raw workflow version definition.
 *
 * A trigger node with no `triggerType` is `InboundPhoneCall`, matching the backend: legacy
 * definitions predate the field. Returns `undefined` for a definition with no trigger node, an
 * unknown trigger type, or one that will not parse — a workflow the resolver cannot place must
 * not be offered as a target.
 */
export function getWorkflowTriggerType(
  rawDefinition: string | null | undefined,
): WorkflowTriggerType | undefined {
  const triggerNode = findTriggerNode(rawDefinition);

  if (!triggerNode) return undefined;

  const configuredTriggerType = triggerNode.data?.triggerType;

  // A stored JSON `null` is absent, not "unknown", matching `declaredTriggerType` in
  // fetch-workflow.ts — diverging would hide a workflow the resolver runs.
  if (configuredTriggerType === undefined || configuredTriggerType === null) {
    return WorkflowTriggerType.InboundPhoneCall;
  }

  return isWorkflowTriggerType(configuredTriggerType) ? configuredTriggerType : undefined;
}

function findTriggerNode(rawDefinition: string | null | undefined) {
  if (!rawDefinition) return undefined;

  try {
    const parsed = JSON.parse(rawDefinition) as DefinitionShape;
    return parsed?.nodes?.find((node) => node?.type === TRIGGER_NODE_TYPE);
  } catch {
    return undefined;
  }
}

/** Narrows a raw string — a stored trigger_type, say — to a trigger this build knows. */
export function isWorkflowTriggerType(value: unknown): value is WorkflowTriggerType {
  return (
    typeof value === 'string' &&
    (Object.values(WorkflowTriggerType) as string[]).includes(value)
  );
}
